import { doc, getDoc, onSnapshot, updateDoc, type FirestoreError } from 'firebase/firestore'

import type {
  ContrastPreference,
  FontPreference,
  FontSize,
  UserPreferences,
} from '@study-decks/shared'

import { getDb } from './firebase.js'

export class PreferencesApiError extends Error {
  override readonly name = 'PreferencesApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  font: 'system',
  fontSize: 'md',
  contrast: 'standard',
  reduceMotion: false,
  ttsVoice: null,
  ttsRate: 1,
}

function isFont(value: unknown): value is FontPreference {
  return (
    value === 'system' || value === 'lexend' || value === 'atkinson' || value === 'opendyslexic'
  )
}

function isFontSize(value: unknown): value is FontSize {
  return value === 'sm' || value === 'md' || value === 'lg' || value === 'xl'
}

function isContrast(value: unknown): value is ContrastPreference {
  return value === 'standard' || value === 'high'
}

function normalizePreferences(value: unknown): UserPreferences {
  if (value === null || typeof value !== 'object') return DEFAULT_PREFERENCES
  const data = value as Partial<Record<keyof UserPreferences, unknown>>
  return {
    font: isFont(data.font) ? data.font : DEFAULT_PREFERENCES.font,
    fontSize: isFontSize(data.fontSize) ? data.fontSize : DEFAULT_PREFERENCES.fontSize,
    contrast: isContrast(data.contrast) ? data.contrast : DEFAULT_PREFERENCES.contrast,
    reduceMotion:
      typeof data.reduceMotion === 'boolean' ? data.reduceMotion : DEFAULT_PREFERENCES.reduceMotion,
    ttsVoice: typeof data.ttsVoice === 'string' ? data.ttsVoice : null,
    ttsRate:
      typeof data.ttsRate === 'number' && data.ttsRate >= 0.5 && data.ttsRate <= 1.5
        ? data.ttsRate
        : DEFAULT_PREFERENCES.ttsRate,
  }
}

function errorCode(err: unknown): string {
  const code = (err as Partial<FirestoreError>).code
  if (code === 'permission-denied') return 'PERMISSION_DENIED'
  if (code === 'not-found') return 'NOT_FOUND'
  if (code === 'unavailable') return 'UNAVAILABLE'
  return 'REQUEST_FAILED'
}

function errorMessage(code: string): string {
  if (code === 'PERMISSION_DENIED') return 'You can only update your own preferences.'
  if (code === 'UNAVAILABLE') return "Couldn't reach the database — check your connection."
  if (code === 'NOT_FOUND') return 'Your profile is not ready yet.'
  return 'Something went wrong — please try again.'
}

function normalizeError(err: unknown): PreferencesApiError {
  if (err instanceof PreferencesApiError) return err
  const code = errorCode(err)
  return new PreferencesApiError(code, errorMessage(code))
}

function userRef(uid: string) {
  return doc(getDb(), 'users', uid)
}

export async function getPreferences(uid: string): Promise<UserPreferences> {
  try {
    const snapshot = await getDoc(userRef(uid))
    return snapshot.exists()
      ? normalizePreferences(snapshot.data()['preferences'])
      : DEFAULT_PREFERENCES
  } catch (err) {
    throw normalizeError(err)
  }
}

export async function updatePreferences(
  uid: string,
  patch: Partial<UserPreferences>,
): Promise<UserPreferences> {
  try {
    const current = await getPreferences(uid)
    const preferences = normalizePreferences({ ...current, ...patch })
    // Rules require the complete preferences map so partial writes cannot drop required keys.
    await updateDoc(userRef(uid), { preferences })
    return preferences
  } catch (err) {
    throw normalizeError(err)
  }
}

export function listenToPreferences(
  uid: string,
  onChange: (preferences: UserPreferences) => void,
): () => void {
  return onSnapshot(
    userRef(uid),
    (snapshot) => {
      onChange(
        snapshot.exists()
          ? normalizePreferences(snapshot.data()['preferences'])
          : DEFAULT_PREFERENCES,
      )
    },
    (err) => {
      throw normalizeError(err)
    },
  )
}

export function applyPreferencesToDocument(preferences: UserPreferences): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.setAttribute('data-font', preferences.font)
  root.setAttribute('data-font-size', preferences.fontSize)
  root.setAttribute('data-contrast', preferences.contrast)
  root.setAttribute('data-motion', preferences.reduceMotion ? 'reduced' : 'normal')
  if (preferences.reduceMotion) {
    root.setAttribute('data-reduce-motion', 'true')
  } else {
    root.removeAttribute('data-reduce-motion')
  }
}
